import type { CommentRoleType } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/shared-types/index.tsx';
import {
	type ObjectUpdateTimelineEvent,
	OBJECT_UPDATE_EVENT,
	OBJECT_COMMENT_EVENT,
	type ObjectChangeType,
	type AddObjectCommentServiceResponse,
	type ObjectCommentTimelineEvent,
	type TimelineEvent,
} from '../../common/types/timeline-event.tsx';
import type { ObjectActivityResponse, AddObjectCommentResponse } from './types.tsx';

const USER_ATTRIBUTE_HISTORY_EVENTS = {
	TYPE_ADDED_USER: 9,
	TYPE_CHANGED_USER: 10,
	TYPE_DELETED_USER: 11,
} as const;

// We need to identify user attribute changes so we can render the profile card
const USER_ATTRIBUTE_CHANGE_TYPES: number[] = Object.values(USER_ATTRIBUTE_HISTORY_EVENTS);

// The Insight backend sends a specific numbered type for each type of attribute update CRUD action
// here we group all of these under the "attribute-updated" type and transform the remaining types
// that don't send back a old / new value or attribute name so we can display a custom message instead
const getChangeType = (updateType: number): ObjectChangeType => {
	switch (updateType) {
		case 0:
			return 'object-created';

		case 7:
			return 'added-attachment';
		case 8:
			return 'deleted-attachment';
		case 24:
			return 'object-type-updated';
		case 25:
			return 'added-avatar';
		case 26:
			return 'changed-avatar';
		case 27:
			return 'deleted-avatar';

		default:
			return 'attribute-updated';
	}
};

const commentRoleMapping: { [key: string]: CommentRoleType } = Object.freeze({
	'0': 'users',
	'1': 'managers',
	'2': 'administrators',
	'3': 'developers',
});

const getRoleType = (roleTypeNumber: number): CommentRoleType => {
	const roleType = commentRoleMapping[`${roleTypeNumber}`];
	if (roleType) {
		return roleType;
	}
	return 'unknown';
};

export const getRoleTypeNumber = (roleType: CommentRoleType): number | undefined => {
	const roleTypeNumber = Object.keys(commentRoleMapping).find(
		(number) => commentRoleMapping[`${number}`] === roleType,
	);

	if (roleTypeNumber) {
		const roleTypeNumberAsInt = Number.parseInt(roleTypeNumber, 10);
		if (Number.isNaN(roleTypeNumberAsInt) === false) {
			return roleTypeNumberAsInt;
		}
	}
	return undefined;
};

export const transformObjectCommentResponse = (
	response: AddObjectCommentResponse,
): ObjectCommentTimelineEvent => ({
	type: OBJECT_COMMENT_EVENT,
	timestamp: Date.parse(response.created),
	data: {
		id: response.id,
		actor: response.actor,
		comment: response.body,
		role: getRoleType(response.role),
		canDelete: response.canDelete,
		canEdit: response.canEdit,
		isEdited: response.updated !== response.created,
		editedTimestamp: Date.parse(response.updated),
	},
});

export const transformAddObjectCommentResponse = (
	response: AddObjectCommentResponse,
): AddObjectCommentServiceResponse => ({
	data: transformObjectCommentResponse(response),
});

export const transformResponse = (response: ObjectActivityResponse | null): TimelineEvent[] => {
	if (!response) {
		return [];
	}

	const data = response.values
		.map((objectActivity) => {
			switch (objectActivity.activityType) {
				case 'comment': {
					return transformObjectCommentResponse(objectActivity.activity);
				}
				case 'history': {
					const objectHistory = objectActivity.activity;
					const objectChangeType = getChangeType(objectHistory.type);
					const isUserAttributeChange = USER_ATTRIBUTE_CHANGE_TYPES.includes(objectHistory.type);

					return {
						type: OBJECT_UPDATE_EVENT,
						timestamp: Date.parse(objectHistory.created),
						data: {
							id: objectHistory.id,
							changeType: objectChangeType,
							actor: objectHistory.actor,
							attributeName: objectHistory.affectedAttribute,
							isUserAttribute: isUserAttributeChange ?? false,
							oldValue: objectHistory.oldValue,
							newValue: objectHistory.newValue,
						},
					};
				}
				default:
					// Do nothing with unknown event types
					return null;
			}
		})
		// Filter out 'null' values
		.filter(
			(timelineEvent): timelineEvent is ObjectUpdateTimelineEvent | ObjectCommentTimelineEvent =>
				!!timelineEvent,
		);

	return data;
};
